<script>
    import { onMount, onDestroy, setContext } from 'svelte';
	import { mapbox, key } from '../mapbox.js';
    import { siteMarkers, selectedJobForEdit, selectedJobs, jobsListStore } from '../../stores.js';
    import { fetchJobs, fetchJobsLive, activeJobs } from '../../data.js';
    import { decodePolyline } from '../../helpers.js';
    import MarkersJob from './MarkersJob.svelte';
    import JobCards from './JobCards.svelte';
    import JobEditor from './JobEditor.svelte';
    import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
    import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";

	setContext(key, {
		getMap: () => map
	});

	export let lat;
	export let lon;
	export let zoom;

    export function filterjobs(){
        console.log("map filterjobs");
        if (child && child.filterjobs) child.filterjobs();
        updateRouteVisibility($selectedJobs);
    }

    const ROUTE_PALETTE = [
        '#2563EB', // vibrant blue
        '#059669', // emerald green
        '#D97706', // warm amber
        '#7C3AED', // deep purple
        '#DB2777', // rose pink
        '#0891B2', // teal cyan
        '#EA580C'  // bright orange
    ];

    let renderedRouteLayers = [];
    let initialJobsData = null;
    let isLoadingJobs = false;

    async function loadJobsData() {
        isLoadingJobs = true;
        try {
            const res = await fetchJobs(['active', 'proposed', 'pending', 'completed', 'cancelled']);
            initialJobsData = res?.jobs || [];
            if (map) {
                renderMaterialPolylines(initialJobsData);
            }
            return res;
        } catch (err) {
            console.warn("[JobsMap] Failed to fetch full jobs list, attempting fallback:", err);
            try {
                const liveRes = await fetchJobsLive().catch(() => null);
                if (liveRes?.jobs?.length) {
                    initialJobsData = liveRes.jobs;
                    if (map) {
                        renderMaterialPolylines(initialJobsData);
                    }
                    return liveRes;
                }
            } catch (e) {}
            const fallbackJobs = ($jobsListStore && $jobsListStore.length > 0)
                ? $jobsListStore
                : ((activeJobs && activeJobs.jobs) ? activeJobs.jobs : []);
            initialJobsData = fallbackJobs;
            if (map && initialJobsData.length > 0) {
                renderMaterialPolylines(initialJobsData);
            }
            return { jobs: fallbackJobs };
        } finally {
            isLoadingJobs = false;
        }
    }

    let promise = loadJobsData();

    // Automatically render polylines once both map and initialJobsData are available
    $: if (map && initialJobsData && initialJobsData.length > 0) {
        renderMaterialPolylines(initialJobsData);
    }

    let container;
	let map;
    let child;
    let showJobsPanel = true;

	function updateIconScale() {
		if (!map || !container) return;
		const z = map.getZoom();
		const size = z < 5 ? 12.5 : z < 10 ? 25 : z < 15 ? 50 : 100;
		container.style.setProperty('--map-icon-size', size + 'px');
	}

    function renderMaterialPolylines(jobs) {
        if (!map || !jobs || !Array.isArray(jobs) || jobs.length === 0) return;

        // Ensure Mapbox style is ready before adding sources and layers
        if (!map.isStyleLoaded()) {
            if (!map.loaded()) {
                map.once('load', () => renderMaterialPolylines(jobs));
            } else {
                setTimeout(() => renderMaterialPolylines(jobs), 150);
            }
            return;
        }

        cleanupRouteLayers();

        const allCoords = [];
        let colorIdx = 0;
        const currentSelected = $selectedJobs || [];

        jobs.forEach((job) => {
            const jNum = job.job_number || job.__job_number__ || (job.id ? `Job #${job.id}` : 'Job');
            const cName = job.customer?.name || (typeof job.customer === 'string' ? job.customer : '');
            const materials = job.materials || [];

            // Collect all candidate identifiers for this job (id, _id, job_number)
            const jobCandidates = [
                job.id,
                job._id,
                job.job_number,
                job.__job_number__
            ].filter(v => v !== null && v !== undefined).map(String);

            // If selectedJobs is not yet initialized or has candidates, show initially
            const isInitialSelected = currentSelected.length === 0
                ? true
                : currentSelected.some(sel => jobCandidates.includes(String(sel)));

            const initialVisibility = isInitialSelected ? 'visible' : 'none';

            materials.forEach((material, matIdx) => {
                const rawPoly = material.polyline || material.load?.polyline;
                if (!rawPoly) return;

                const coords = decodePolyline(rawPoly);
                if (!coords || coords.length < 2) return;

                coords.forEach(pt => allCoords.push(pt));

                const color = ROUTE_PALETTE[colorIdx % ROUTE_PALETTE.length];
                colorIdx++;

                const matKey = String(material.job_material_number || material.id || `${job.id || 'j'}_${matIdx}`).replace(/[^a-zA-Z0-9_-]/g, '_');
                const sourceId = `route-source-${matKey}`;
                const casingLayerId = `route-casing-${matKey}`;
                const lineLayerId = `route-line-${matKey}`;

                // Remove existing if present
                if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
                if (map.getLayer(casingLayerId)) map.removeLayer(casingLayerId);
                if (map.getSource(sourceId)) map.removeSource(sourceId);

                // Add GeoJSON LineString Source
                map.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {
                            jobNumber: jNum,
                            materialName: material.name || 'Material',
                            customerName: cName
                        },
                        geometry: {
                            type: 'LineString',
                            coordinates: coords
                        }
                    }
                });

                // Add casing layer for high contrast on streets
                map.addLayer({
                    id: casingLayerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                        'visibility': initialVisibility
                    },
                    paint: {
                        'line-color': '#FFFFFF',
                        'line-width': 7,
                        'line-opacity': 0.75
                    }
                });

                // Add primary colored polyline
                map.addLayer({
                    id: lineLayerId,
                    type: 'line',
                    source: sourceId,
                    layout: {
                        'line-join': 'round',
                        'line-cap': 'round',
                        'visibility': initialVisibility
                    },
                    paint: {
                        'line-color': color,
                        'line-width': 4.5,
                        'line-opacity': 0.88
                    }
                });

                renderedRouteLayers.push({
                    jobCandidates,
                    sourceId,
                    casingLayerId,
                    lineLayerId
                });
            });
        });

        // Fit map bounds to encompass all initial material polylines
        if (allCoords.length >= 2) {
            const bounds = new mapbox.LngLatBounds();
            allCoords.forEach(coord => bounds.extend(coord));
            map.fitBounds(bounds, {
                padding: { top: 70, bottom: 70, left: 70, right: 70 },
                maxZoom: 13
            });
        }
    }

    /**
     * Toggles visibility of material polyline layers based on the selected jobs list.
     * When a job is checked in JobCards, its route appears; when unchecked, it hides.
     */
    function updateRouteVisibility(selected) {
        if (!map || renderedRouteLayers.length === 0) return;
        if (!map.isStyleLoaded()) {
            if (!map.loaded()) {
                map.once('load', () => updateRouteVisibility(selected));
            } else {
                setTimeout(() => updateRouteVisibility(selected), 150);
            }
            return;
        }

        const selectedList = (selected || []).map(String);

        renderedRouteLayers.forEach(({ jobCandidates, casingLayerId, lineLayerId }) => {
            const isVisible = selectedList.some(c => jobCandidates.includes(c));
            const visValue = isVisible ? 'visible' : 'none';
            try {
                if (map.getLayer(casingLayerId)) {
                    map.setLayoutProperty(casingLayerId, 'visibility', visValue);
                }
                if (map.getLayer(lineLayerId)) {
                    map.setLayoutProperty(lineLayerId, 'visibility', visValue);
                }
            } catch (err) {}
        });
    }

    // Reactively show/hide polylines whenever selectedJobs store updates
    $: if (map && renderedRouteLayers.length > 0) {
        updateRouteVisibility($selectedJobs);
    }

    function cleanupRouteLayers() {
        if (!map) return;
        renderedRouteLayers.forEach(({ sourceId, casingLayerId, lineLayerId }) => {
            try {
                if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
                if (map.getLayer(casingLayerId)) map.removeLayer(casingLayerId);
                if (map.getSource(sourceId)) map.removeSource(sourceId);
            } catch (err) {}
        });
        renderedRouteLayers = [];
    }

	function load() {
		if (map || !container) return;
		map = new mapbox.Map({
			container,
			style: 'mapbox://styles/mapbox/streets-v9',
            center: [lon, lat],
			zoom: zoom
		});
		updateIconScale();
		map.on('zoom', updateIconScale);
        map.on('load', () => {
            if (initialJobsData && initialJobsData.length > 0) {
                renderMaterialPolylines(initialJobsData);
            }
            if ($selectedJobs && $selectedJobs.length > 0) {
                updateRouteVisibility($selectedJobs);
            }
        });
	}

    onMount(() => {
        if (!map && container) {
            load();
        }
        if (!initialJobsData || initialJobsData.length === 0) {
            loadJobsData();
        }
    });

    function refit() {
        const bbox = getBoundingBoxCoordinates();
        if(!bbox) return; // added because svelte calls afterUpdate twice https://github.com/sveltejs/svelte/issues/6016
        map.fitBounds(bbox, {
            padding: {top: 50, bottom:50, left: 50, right: 50}
        });     
    }

    function getBoundingBoxCoordinates() {
        let siteMarkersSub;

        siteMarkers.subscribe((value) => {
            siteMarkersSub = value;
        });

        const coordinates = siteMarkersSub.map(marker => [marker.marker.getLngLat().lat, marker.marker.getLngLat().lng]);
        if (coordinates.length === 0) {
            return null;
        }

        let minX = coordinates[0][0];
        let minY = coordinates[0][1];
        let maxX = coordinates[0][0];
        let maxY = coordinates[0][1];

        for (let i = 0; i < coordinates.length; i++) {
            const [x, y] = coordinates[i];
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        }

        const topLeft = [minY, minX];
        const topRight = [maxX, minY];
        const bottomLeft = [minX, maxY];
        const bottomRight = [maxY, maxX];
        return [ topLeft, bottomRight ];
    }

	function jobCardClick(e){
        let response = e;
		if (child && child.filterjobs) child.filterjobs();
        updateRouteVisibility($selectedJobs);
	}

    function handleJobSaved(e) {
        if (child && child.filterjobs) {
            child.filterjobs();
        }
        loadJobsData();
    }

	onDestroy(() => {
        cleanupRouteLayers();
		if (map) map.remove();
	});
</script>

<!-- this special element will be explained in a later section -->
<svelte:head>
	<link rel="stylesheet" href="https://unpkg.com/mapbox-gl/dist/mapbox-gl.css" on:load={load} />
</svelte:head>

<div class="mapbox-root">
    <div bind:this={container} class="mapbox-map-canvas" style="--map-icon-size: {zoom < 5 ? '12.5px' : zoom < 10 ? '25px' : zoom < 15 ? '50px' : '100px'};"></div>

    {#if isLoadingJobs}
        <div class="map-loading-indicator">
            <span class="loading-spinner"></span>
            <span>Loading active jobs...</span>
        </div>
    {/if}

    {#if map}
        <slot />
        <div class="jobs-floating-card" hidden={!showJobsPanel}>
            <div class="jobs-card-header">
                <div class="header-titles">
                    <h3 class="card-title">Haulage & Fleet Operations</h3>
                    <span class="card-subtitle">Manage jobs, view sites & live fleet movements</span>
                </div>
                <button class="close-panel-btn" on:click={() => showJobsPanel = false} title="Hide Jobs Panel" aria-label="Hide Jobs Panel" type="button">
                    <ChevronLeft size={20} />
                </button>
            </div>
            <div class="jobs-card-content">
                <JobCards 
                    on:job-show={(e) => jobCardClick(e)}
                    on:job-select={(e) => $selectedJobForEdit = e.detail.job}
                    on:truck-select={(e) => {
                        const coords = e.detail?.coords;
                        if (coords && coords.lat != null && coords.lng != null && map) {
                            map.flyTo({ center: [coords.lng, coords.lat], zoom: Math.max(map.getZoom(), 12), speed: 1.2 });
                        }
                    }}
                    on:job-deleted={(e) => {
                        if ($selectedJobForEdit && ($selectedJobForEdit.id === e.detail.id || $selectedJobForEdit.__job_number__ === e.detail.id)) {
                            $selectedJobForEdit = null;
                        }
                        if (child && child.filterjobs) child.filterjobs();
                    }}
                />
            </div>
        </div>
        {#if !showJobsPanel}
            <button class="show-jobs-btn" on:click={() => showJobsPanel = true} type="button">
                <ChevronRight size={18} />
                <span>View Jobs & Fleet</span>
            </button>
        {/if}
        {#if $selectedJobForEdit}
            <JobEditor 
                job={$selectedJobForEdit}
                {map}
                on:close={() => $selectedJobForEdit = null}
                on:job-saved={handleJobSaved}
                on:job-deleted={(e) => {
                    $selectedJobForEdit = null;
                    if (child && child.filterjobs) child.filterjobs();
                }}
            />
        {/if}
        <MarkersJob bind:this={child}/>
    {/if}
</div>

<style>
	.mapbox-root {
        position: relative;
		width: 100%;
		height: 100%;
        overflow: hidden;
	}

    .mapbox-map-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
    }

    .map-loading-indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #ffffff;
        padding: 12px 20px;
        border-radius: var(--tt-radius-md);
        box-shadow: var(--tt-shadow-lg);
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 14px;
        font-weight: 500;
        color: var(--tt-slate-700);
        z-index: 1000;
    }

    .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid var(--tt-slate-200);
        border-top-color: var(--tt-primary);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .jobs-floating-card {
        position: absolute;
        top: 1rem;
        left: 1rem;
        width: 540px;
        max-width: calc(100vw - 2rem);
        max-height: calc(100% - 2rem);
        background: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: 16px;
        box-shadow: 0 4px 24px -2px rgba(0, 0, 0, 0.08), 0 2px 8px -1px rgba(0, 0, 0, 0.04);
        z-index: 1200;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: fadeIn 0.2s ease-out;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
    }

    .jobs-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 18px;
        border-bottom: 1px solid var(--tt-slate-200);
        background-color: #ffffff;
    }

    .card-title {
        margin: 0;
        font-size: 1rem;
        font-weight: 700;
        color: var(--tt-slate-900);
        letter-spacing: -0.01em;
    }

    .card-subtitle {
        font-size: 0.8rem;
        color: var(--tt-slate-500);
        margin-top: 1px;
    }

    .jobs-floating-card[hidden] {
        display: none !important;
    }

    .close-panel-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        color: var(--tt-slate-500);
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        margin: 0;
        padding: 0;
        flex-shrink: 0;
        pointer-events: auto;
    }

    .close-panel-btn :global(svg) {
        pointer-events: none;
    }

    .close-panel-btn:hover {
        background: var(--tt-slate-100);
        color: var(--tt-slate-800);
    }

    .jobs-card-content {
        overflow-y: auto;
        padding: 12px;
        scrollbar-width: thin;
        scrollbar-color: var(--tt-slate-300) transparent;
    }

    .jobs-card-content::-webkit-scrollbar {
        width: 5px;
    }

    .jobs-card-content::-webkit-scrollbar-thumb {
        background: var(--tt-slate-300);
        border-radius: 4px;
    }

    .show-jobs-btn {
        position: absolute;
        top: 1rem;
        left: 1rem;
        z-index: 1200;
        background: #ffffff;
        color: var(--tt-slate-700);
        border: 1px solid var(--tt-slate-200);
        border-radius: 9999px;
        padding: 8px 16px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        box-shadow: var(--tt-shadow-md);
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .show-jobs-btn:hover {
        background: var(--tt-slate-50);
        color: var(--tt-primary);
        box-shadow: var(--tt-shadow-lg);
        transform: translateY(-1px);
    }
</style>
